// Sentence checker: grades a Prakrit sentence by grammar, not by string.
// Checks पुरुष/वचन agreement between subject and verb, the case of objects and the words a task asks for.
// Any word order is accepted. Feedback is rule-based Hindi + English text.

import type {
    Analysis,
    AsAnalysis,
    FormIndex,
    VerbAnalysis
} from '@/prakrit/lib/grammar/analyser'
import type {
    Case,
    Gender,
    Num,
    Person,
    Tense
} from '@/prakrit/lib/grammar/types'

import { cleanToken, tokenize } from '@/prakrit/lib/grammar/devanagari'
import { analyse, formIndex, isFinite } from '@/prakrit/lib/grammar/analyser'
import { resolveRef } from '@/prakrit/lib/grammar/formref'
import { pronounMeta } from '@/prakrit/lib/grammar/pronouns'
import {
    CASE_LABEL,
    KARAK,
    NUMBER_LABEL,
    PERSON_LABEL,
    PERSON_SHORT,
    SUBJECT
} from '@/prakrit/lib/grammar/labels'

export interface SentenceSpec {
    subject?: { person: Person, number: Num, gender?: Gender, lemma?: string }
    verb: { root: string, tense: Tense | 'bhoot' | 'vartaman' }
    objects?: { stem: string, case: Case, number: Num }[]
    adverbs?: string[]
    /** Every word must be one the task asks for (default true when a spec is given). */
    exact?: boolean
}

export type IssueCode =
    | 'empty'
    | 'unknown_word'
    | 'unreviewed_word'
    | 'no_verb'
    | 'two_verbs'
    | 'wrong_verb'
    | 'wrong_tense'
    | 'agreement_person'
    | 'agreement_number'
    | 'wrong_subject'
    | 'missing_subject'
    | 'wrong_case'
    | 'missing_word'
    | 'extra_word'
    | 'variant_form'

export interface Issue {
    code: IssueCode
    hi: string
    en: string
    token?: string
    expected?: string
}

export type Role = 'verb' | 'subject' | 'predicate' | 'object' | 'adverb' | 'other'

export interface TokenResult {
    token: string
    analyses: Analysis[]
    role?: Role
    chosen?: Analysis
}

export interface SentenceResult {
    ok: boolean
    issues: Issue[]
    notes: Issue[]
    tokens: TokenResult[]
}

export interface CheckOptions {
    /** Review-status filter: only analyses it accepts may be used (graded content). */
    allow?: (a: Analysis) => boolean
    index?: FormIndex
}

const PRIORITY: Record<IssueCode, number> = {
    empty: 0,
    unknown_word: 0,
    unreviewed_word: 1,
    no_verb: 2,
    two_verbs: 3,
    wrong_verb: 4,
    wrong_tense: 5,
    agreement_person: 6,
    agreement_number: 6,
    wrong_subject: 7,
    missing_subject: 8,
    wrong_case: 9,
    missing_word: 10,
    extra_word: 11,
    variant_form: 99
}

type Finite = VerbAnalysis | AsAnalysis

const COPULAS = new Set(['अस', 'हो', 'हव'])

const sameLemma = (a: Analysis, stem: string): boolean => {
    if (a.pos === 'pronoun') {
        const meta = pronounMeta(stem)

        return meta ? meta.key === a.lemma : a.lemma === stem
    }

    return a.lemma === stem
}

/** The core form a verb needs for a person and number. */
export const expectedVerb = (root: string, tense: Tense, person: Person, number: Num): string => {
    const ref = root === 'अस'
        ? { kind: 'as' as const, tense: tense === 'bhoot' ? 'bhoot' as const : 'vartaman' as const, person, number }
        : { kind: 'verb' as const, root, tense, person, number }

    return resolveRef(ref).display
}

const describe = (person: Person, number: Num): { hi: string, en: string } => ({
    hi: `${PERSON_LABEL[person].hi} ${NUMBER_LABEL[number].hi}`,
    en: `${PERSON_LABEL[person].hi} ${NUMBER_LABEL[number].hi} (${PERSON_SHORT[person].en}, ${NUMBER_LABEL[number].en})`
})

const subjectWord = (spec: NonNullable<SentenceSpec['subject']>): string => {
    if (spec.lemma) {
        const meta = pronounMeta(spec.lemma)
        const r = meta
            ? resolveRef({ kind: 'pronoun', table: meta.key, case: 'prathama', number: spec.number })
            : resolveRef({ kind: 'noun', stem: spec.lemma, case: 'prathama', number: spec.number })

        if (r.ok) return r.display
    }

    if (spec.person === 'anya' && spec.gender === 'stri') return spec.number === 'eka' ? 'सा' : 'ताओ'

    return SUBJECT[spec.person][spec.number]
}

const nominatives = (list: Analysis[]): Analysis[] => list.filter(a => (a.pos === 'noun' || a.pos === 'pronoun') && a.case === 'prathama'
    || (a.pos === 'number'))

const accusatives = (list: Analysis[]): Analysis[] => list.filter(a => (a.pos === 'noun' || a.pos === 'pronoun') && a.case === 'dvitiya')

const agrees = (a: Analysis, v: Finite): boolean => {
    if (a.pos === 'number') {
        const n: Num = a.value === 1 ? 'eka' : 'bahu'

        return v.person === 'anya' && v.number === n
    }

    if (a.pos !== 'noun' && a.pos !== 'pronoun') return false

    const person: Person = a.pos === 'pronoun' ? a.person : 'anya'

    return person === v.person && a.number === v.number
}

interface Attempt {
    issues: Issue[]
    notes: Issue[]
    roles: { role: Role, chosen?: Analysis }[]
}

const tryVerb = (entries: TokenResult[], vi: number, va: Finite, spec?: SentenceSpec): Attempt => {
    const issues: Issue[] = []
    const notes: Issue[] = []
    const roles: Attempt['roles'] = entries.map(() => ({ role: 'other' as Role }))
    const copula = COPULAS.has(va.lemma)
    const verbToken = entries[vi].token

    roles[vi] = { role: 'verb', chosen: va }

    if (va.status === 'alt') {
        const core = expectedVerb(va.lemma, va.tense, va.person, va.number)

        notes.push({
            code: 'variant_form',
            token: verbToken,
            expected: core,
            hi: `${verbToken} विकल्प रूप है (सही है); पहले सिखाया जाने वाला रूप ${core} है।`,
            en: `${verbToken} is a variant form (accepted); the form taught first is ${core}.`
        })
    }

    if (spec) {
        if (va.lemma !== spec.verb.root) {
            const want = expectedVerb(spec.verb.root, spec.verb.tense as Tense, spec.subject?.person ?? va.person, spec.subject?.number ?? va.number)

            issues.push({
                code: 'wrong_verb',
                token: verbToken,
                expected: want,
                hi: `यहाँ धातु ${spec.verb.root} चाहिए → ${want}`,
                en: `This sentence needs the root ${spec.verb.root} → ${want}`
            })
        }
        else if (va.tense !== spec.verb.tense) {
            const want = expectedVerb(spec.verb.root, spec.verb.tense as Tense, spec.subject?.person ?? va.person, spec.subject?.number ?? va.number)

            issues.push({
                code: 'wrong_tense',
                token: verbToken,
                expected: want,
                hi: `${verbToken} दूसरे काल का रूप है; यहाँ यह रूप चाहिए → ${want}`,
                en: `${verbToken} is in a different tense; this sentence needs → ${want}`
            })
        }
    }

    const specObjects = [...(spec?.objects ?? [])]
    const specAdverbs = [...(spec?.adverbs ?? [])]
    let subjectMatched = false
    let anySubject = false

    entries.forEach((entry, i) => {
        if (i === vi) return

        const list = entry.analyses

        // 1. A word the task names as an object.
        const objIndex = specObjects.findIndex(o => list.some(a => sameLemma(a, o.stem)))

        if (objIndex >= 0) {
            const o = specObjects[objIndex]
            const hit = list.find(a => sameLemma(a, o.stem) && (a.pos === 'noun' || a.pos === 'pronoun') && a.case === o.case && a.number === o.number)

            specObjects.splice(objIndex, 1)

            if (hit) {
                roles[i] = { role: 'object', chosen: hit }

                return
            }

            const meta = pronounMeta(o.stem)
            const want = meta
                ? resolveRef({ kind: 'pronoun', table: meta.key, case: o.case as Exclude<Case, 'sambodhan'>, number: o.number }).display
                : resolveRef({ kind: 'noun', stem: o.stem, case: o.case, number: o.number }).display
            const have = list.find(a => sameLemma(a, o.stem))
            const haveCase = have && (have.pos === 'noun' || have.pos === 'pronoun') ? have.case : null

            roles[i] = { role: 'object', chosen: have }
            issues.push({
                code: 'wrong_case',
                token: entry.token,
                expected: want,
                hi: haveCase
                    ? `${entry.token} ${CASE_LABEL[haveCase].hi} विभक्ति है; ${KARAK[o.case].hi} के लिए ${CASE_LABEL[o.case].hi} ${NUMBER_LABEL[o.number].hi} चाहिए → ${want}`
                    : `यहाँ ${CASE_LABEL[o.case].hi} ${NUMBER_LABEL[o.number].hi} चाहिए → ${want}`,
                en: haveCase
                    ? `${entry.token} is ${CASE_LABEL[haveCase].hi} (${CASE_LABEL[haveCase].en}); ${KARAK[o.case].en.split(' (')[0]} needs ${CASE_LABEL[o.case].hi} ${NUMBER_LABEL[o.number].hi} → ${want}`
                    : `This needs ${CASE_LABEL[o.case].hi} ${NUMBER_LABEL[o.number].hi} → ${want}`
            })

            return
        }

        // 2. An अव्यय the task asks for.
        const adv = list.find(a => a.pos === 'avyaya')
        const advIndex = adv ? specAdverbs.findIndex(w => w === entry.token || w === adv.lemma) : -1

        if (adv && advIndex >= 0) {
            specAdverbs.splice(advIndex, 1)
            roles[i] = { role: 'adverb', chosen: adv }

            return
        }

        // 3. Nominatives: the subject (or, with a copula, a predicate).
        const noms = nominatives(list)

        if (noms.length > 0) {
            const agreeing = noms.find(a => agrees(a, va))

            if (agreeing) {
                roles[i] = { role: 'subject', chosen: agreeing }
                anySubject = true

                if (spec?.subject) {
                    const s = spec.subject
                    const person: Person = agreeing.pos === 'pronoun' ? agreeing.person : 'anya'
                    const okLemma = !s.lemma || sameLemma(agreeing, s.lemma)
                    const okGender = !s.gender || agreeing.pos !== 'pronoun' || !agreeing.gender || agreeing.gender === s.gender

                    if (person === s.person && (agreeing.pos === 'number' ? true : (agreeing as { number: Num }).number === s.number) && okLemma && okGender) {
                        subjectMatched = true
                    }
                }

                return
            }

            // The task names the subject (e.g. ते = अन्य पुरुष बहुवचन): read this word as that subject,
            // so a wrong verb gets the agreement reason instead of "ते" being taken as an object ("them").
            const intended = spec?.subject
                ? noms.find(a => (a.pos === 'pronoun' ? a.person : 'anya') === spec.subject?.person
                    && (a.pos === 'number' ? (a.value === 1 ? 'eka' : 'bahu') : (a as { number: Num }).number) === spec.subject?.number)
                : undefined

            if (intended) {
                const p: Person = intended.pos === 'pronoun' ? intended.person : 'anya'
                const n: Num = intended.pos === 'number' ? (intended.value === 1 ? 'eka' : 'bahu') : (intended as { number: Num }).number
                const want = expectedVerb(va.lemma, va.tense, p, n)
                const d = describe(p, n)

                roles[i] = { role: 'subject', chosen: intended }
                anySubject = true
                subjectMatched = true
                issues.push({
                    code: p !== va.person ? 'agreement_person' : 'agreement_number',
                    token: entry.token,
                    expected: want,
                    hi: `${entry.token} ${d.hi} है → ${want}`,
                    en: `${entry.token} is ${d.en} → ${want}`
                })

                return
            }

            const acc = accusatives(list)

            if (acc.length > 0 && !spec?.subject) {
                roles[i] = { role: 'object', chosen: acc[0] }

                return
            }

            const first = noms[0]
            const firstPerson: Person = first.pos === 'pronoun' ? first.person : 'anya'
            const firstNumber: Num = first.pos === 'number' ? (first.value === 1 ? 'eka' : 'bahu') : (first as { number: Num }).number

            if (copula && !(first.pos === 'pronoun' && first.person !== 'anya')) {
                roles[i] = { role: 'predicate', chosen: first }

                return
            }

            if (acc.length > 0 && spec?.subject && !(first.pos === 'pronoun' && first.person !== 'anya')) {
                roles[i] = { role: 'object', chosen: acc[0] }

                return
            }

            roles[i] = { role: 'subject', chosen: first }
            anySubject = true

            const tenseForVerb: Tense = va.pos === 'as' ? va.tense : va.tense
            const want = expectedVerb(va.lemma, tenseForVerb, firstPerson, firstNumber)
            const d = describe(firstPerson, firstNumber)

            issues.push({
                code: firstPerson !== va.person ? 'agreement_person' : 'agreement_number',
                token: entry.token,
                expected: want,
                hi: `${entry.token} ${d.hi} है → ${want}`,
                en: `${entry.token} is ${d.en} → ${want}`
            })

            return
        }

        // 4. Objects without a task, अव्यय, कृदन्त and other cases.
        const acc = accusatives(list)

        if (acc.length > 0) {
            roles[i] = { role: 'object', chosen: acc[0] }

            if (spec?.exact !== false && spec) {
                issues.push({ code: 'extra_word', token: entry.token, hi: `यह शब्द इस वाक्य में नहीं चाहिए: ${entry.token}`, en: `This word is not needed here: ${entry.token}` })
            }

            return
        }

        if (list.some(isFinite)) {
            issues.push({ code: 'two_verbs', token: entry.token, hi: `एक वाक्य में एक ही क्रिया रखें: ${verbToken} या ${entry.token}`, en: `Use one verb in this sentence: ${verbToken} or ${entry.token}` })

            return
        }

        roles[i] = { role: list[0]?.pos === 'avyaya' ? 'adverb' : 'other', chosen: list[0] }

        if (spec && spec.exact !== false) {
            issues.push({ code: 'extra_word', token: entry.token, hi: `यह शब्द इस वाक्य में नहीं चाहिए: ${entry.token}`, en: `This word is not needed here: ${entry.token}` })
        }
    })

    if (spec?.subject && !subjectMatched) {
        const word = subjectWord(spec.subject)
        const d = describe(spec.subject.person, spec.subject.number)

        if (anySubject) {
            issues.push({ code: 'wrong_subject', expected: word, hi: `इस वाक्य का कर्ता ${word} है (${d.hi})`, en: `The subject of this sentence is ${word} (${d.en})` })
        }
        else {
            issues.push({ code: 'missing_subject', expected: word, hi: `कर्ता ${word} जोड़ें (${d.hi})`, en: `Add the subject ${word} (${d.en})` })
        }
    }

    for (const o of specObjects) {
        const meta = pronounMeta(o.stem)
        const want = meta
            ? resolveRef({ kind: 'pronoun', table: meta.key, case: o.case as Exclude<Case, 'sambodhan'>, number: o.number }).display
            : resolveRef({ kind: 'noun', stem: o.stem, case: o.case, number: o.number }).display

        issues.push({ code: 'missing_word', expected: want, hi: `${KARAK[o.case].hi} ${want} जोड़ें`, en: `Add the word ${want} (${KARAK[o.case].en})` })
    }

    for (const w of specAdverbs) {
        issues.push({ code: 'missing_word', expected: w, hi: `${w} जोड़ें`, en: `Add ${w}` })
    }

    return { issues, notes, roles }
}

const sortIssues = (issues: Issue[]) => [...issues].sort((a, b) => PRIORITY[a.code] - PRIORITY[b.code])

export const checkSentence = (input: string | string[], spec?: SentenceSpec, options: CheckOptions = {}): SentenceResult => {
    const index = options.index ?? formIndex()
    const allow = options.allow ?? (() => true)
    const words = Array.isArray(input) ? input.map(cleanToken).filter(Boolean) : tokenize(input)

    if (words.length === 0) {
        return { ok: false, issues: [{ code: 'empty', hi: 'पहले शब्द चुनें।', en: 'Choose some words first.' }], notes: [], tokens: [] }
    }

    const tokens: TokenResult[] = []

    for (const token of words) {
        const all = analyse(token, index)
        const allowed = all.filter(allow)

        if (all.length === 0) {
            return {
                ok: false,
                issues: [{ code: 'unknown_word', token, hi: `यह शब्द पहचाना नहीं गया: ${token}`, en: `This word is not recognised: ${token}` }],
                notes: [],
                tokens: [...tokens, { token, analyses: [] }]
            }
        }

        if (allowed.length === 0) {
            return {
                ok: false,
                issues: [{ code: 'unreviewed_word', token, hi: `${token} अभी शिक्षक की समीक्षा में है।`, en: `${token} is still awaiting a teacher's review.` }],
                notes: [],
                tokens: [...tokens, { token, analyses: [] }]
            }
        }

        tokens.push({ token, analyses: allowed })
    }

    const verbIdx = tokens.map((t, i) => (t.analyses.some(isFinite) ? i : -1)).filter(i => i >= 0)

    if (verbIdx.length === 0) {
        return {
            ok: false,
            issues: [{ code: 'no_verb', hi: 'वाक्य में क्रिया नहीं है।', en: 'The sentence has no verb.' }],
            notes: [],
            tokens
        }
    }

    let best: (Attempt & { vi: number }) | null = null

    const score = (a: Attempt) => a.issues.reduce((sum, issue) => sum + 100 - PRIORITY[issue.code], 0) * 10 + a.notes.length

    for (const vi of verbIdx) {
        for (const va of tokens[vi].analyses.filter(isFinite) as Finite[]) {
            const attempt = tryVerb(tokens, vi, va, spec)

            if (!best || score(attempt) < score(best)) best = { ...attempt, vi }
        }
    }

    const result = best as Attempt & { vi: number }
    const issues = sortIssues(result.issues)

    return {
        ok: issues.length === 0,
        issues,
        notes: result.notes,
        tokens: tokens.map((t, i) => ({ ...t, role: result.roles[i]?.role, chosen: result.roles[i]?.chosen }))
    }
}

/** The first failing rule, or null when the sentence is correct. */
export const firstIssue = (result: SentenceResult): Issue | null => result.issues[0] ?? null
